/**
 * SHARED
 * Cross-cutting code usable by any module. Must not import from any module.
 */

import { useState } from "react";
import { useBilling } from "../hooks/useBilling.js";
import { startCheckout, openBillingPortal } from "../utils/stripeLinks.js";
import {
  TRIAL_DAYS, TIERS, SEAT_BANDS, lookupKey, formatUsd, CONTACT_EMAIL,
} from "../billingConfig.js";

// Subscribe screen: shown to signed-in users without active billing. Also
// reachable as the "Manage billing" entry point for active subs.
export default function Subscribe() {
  const { subscription, loading, refresh } = useBilling();
  const [busy, setBusy]         = useState(false);
  const [err, setErr]           = useState(null);
  const [band, setBand]         = useState("1to5");
  const [interval, setInterval] = useState("monthly");

  if (loading) return <p className="muted">Loading...</p>;

  const status = subscription?.status ?? null;
  const isLapsed = status === "past_due" || status === "canceled";

  async function handleCheckout(key) {
    setBusy(true); setErr(null);
    try {
      await startCheckout({ lookupKey: key });
    } catch (e) {
      setErr(e.message || "Could not start checkout.");
    } finally {
      setBusy(false);
    }
  }

  async function handlePortal() {
    setBusy(true); setErr(null);
    try {
      await openBillingPortal();
    } catch (e) {
      setErr(e.message || "Could not open billing portal.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="auth-card">
      <h1>{subscription ? "Your plan" : `Start your ${TRIAL_DAYS}-day free trial`}</h1>

      {!subscription && (
        <>
          <p>
            Full access for {TRIAL_DAYS} days. No credit card required to start —
            add one anytime to keep your workspace live after the trial.
          </p>

          <div className="plan-controls">
            <label>
              <span>Team size</span>
              <select value={band} onChange={(e) => setBand(e.target.value)}>
                {SEAT_BANDS.map((b) => (
                  <option key={b.key} value={b.key}>{b.label}</option>
                ))}
              </select>
            </label>
            <label>
              <span>Billing</span>
              <select value={interval} onChange={(e) => setInterval(e.target.value)}>
                <option value="monthly">Monthly</option>
                <option value="annual">Annual — 2 months free</option>
              </select>
            </label>
          </div>

          <div className="plan-row">
            {TIERS.map((tier) => {
              const cents = tier.prices[band]?.[interval];
              // Starter has no 11–20 band; show why rather than hiding the card.
              const unavailable = cents == null;
              return (
                <div className="plan-card" key={tier.key}>
                  <h2>{tier.name}</h2>
                  <p className="plan-blurb">{tier.blurb}</p>
                  {unavailable ? (
                    <p className="muted">
                      {tier.name} covers up to {tier.userCap} users. Choose a smaller
                      team size, or pick {tier.key === "starter" ? "Growth" : "another plan"}.
                    </p>
                  ) : (
                    <p className="plan-price">
                      <strong>{formatUsd(cents)}</strong>
                      <span className="muted"> / {interval === "monthly" ? "mo" : "yr"}</span>
                    </p>
                  )}
                  <ul className="plan-modules">
                    {tier.modules.map((m) => <li key={m}>{m}</li>)}
                  </ul>
                  <button className="btn"
                    disabled={busy || unavailable}
                    onClick={() => handleCheckout(lookupKey(tier.key, band, interval))}
                  >
                    Start free trial
                  </button>
                </div>
              );
            })}
          </div>

          <p className="muted">
            More than 20 users? <a href={`mailto:${CONTACT_EMAIL}`}>Talk to us</a> — we'll
            put together a plan that fits.
          </p>
        </>
      )}

      {subscription && (
        <>
          <p>
            Status: <strong>{status}</strong>
            {subscription.trial_ends_at && (status === "trialing") && (
              <> &nbsp;·&nbsp; trial ends {new Date(subscription.trial_ends_at).toLocaleDateString()}</>
            )}
          </p>
          {isLapsed && (
            <p className="warning">
              Your subscription is {status}. Embeds may stop serving after the grace period.
            </p>
          )}
          <button className="btn" disabled={busy} onClick={handlePortal}>Manage billing</button>
          <button disabled={busy} className="link" onClick={refresh}>Refresh status</button>
        </>
      )}

      {err && <p className="error">{err}</p>}
    </section>
  );
}
